import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import DistinctiveMarkForm from './DistinctiveMarkForm';

function DistinctiveMarks({ juvenileId }) {
  const [marks, setMarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMark, setEditingMark] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchMarks = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/juveniles/${juvenileId}/marks`);
      setMarks(response.data);
    } catch (err) {
      setError('Failed to fetch distinctive marks.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchMarks();
  }, [fetchMarks]);
  
  const handleFormSubmit = async (formData) => {
    try {
      if (editingMark) {
        await apiClient.put(`/api/juveniles/marks/${editingMark.id}`, formData);
      } else {
        await apiClient.post(`/api/juveniles/${juvenileId}/marks`, formData);
      }
      setIsModalOpen(false);
      setEditingMark(null);
      fetchMarks();
    } catch (err) {
      console.error('Failed to save mark:', err);
      alert('Failed to save mark.');
    }
  };

  const handleDelete = async (markId) => {
    if (window.confirm('Are you sure you want to delete this mark?')) {
      try {
        await apiClient.delete(`/api/juveniles/marks/${markId}`);
        fetchMarks();
      } catch (err) {
        console.error('Failed to delete mark:', err);
        alert('Failed to delete mark.');
      }
    }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-slate-700">Distinctive Marks</h3>
        {hasPermission('manage-distinctive-marks') && (
          <button onClick={() => setIsModalOpen(true)} className="text-sm bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-1 px-3 rounded-lg">
            + Add Mark
          </button>
        )}
      </div>
      <div className="mt-2 border-t pt-2 space-y-2">
        {isLoading && <p>Loading marks...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {marks.length > 0 ? (
          marks.map(mark => (
            <div key={mark.id} className="text-sm text-slate-600 flex justify-between items-center p-2 rounded hover:bg-slate-100">
              <div>
                <span className="font-semibold">{mark.mark_type} on {mark.location || 'N/A'}:</span> {mark.description}
              </div>
              {hasPermission('manage-distinctive-marks') && (
                <div className="flex gap-2">
                  <button onClick={() => { setEditingMark(mark); setIsModalOpen(true); }} className="text-xs font-semibold hover:text-gold">EDIT</button>
                  <button onClick={() => handleDelete(mark.id)} className="text-xs font-semibold hover:text-red-600">DELETE</button>
                </div>
              )}
            </div>
          ))
        ) : (
          !isLoading && <p className="text-sm text-slate-500">No distinctive marks recorded.</p>
        )}
      </div>

      {isModalOpen && (
        <DistinctiveMarkForm
          initialData={editingMark || {}}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingMark(null); }}
        />
      )}
    </div>
  );
}

export default DistinctiveMarks;